import React, { useEffect, useState } from "react";
import { Download, Loader2, Plus, StickyNote } from "lucide-react";
import Eye from "lucide-react/dist/esm/icons/eye";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { gbp, ukDate, ukDateTime } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { SLOT_LABEL, downloadQuoteFile, fmtBytes } from "../../lib/subby";
import { Modal, PrimaryButton, QuietButton } from "../UI";

function Row({ label, children, testid }) {
  return (
    <div className="flex items-baseline gap-3" style={{ fontSize: 12.5 }}>
      <span className="col-head flex-shrink-0" style={{ fontSize: 9.5, width: 96 }}>{label}</span>
      <span className="text-ink min-w-0" data-testid={testid}>{children}</span>
    </div>
  );
}

function Files({ tenderId, quote, slot, onView }) {
  const revs = quote.files?.[slot] || [];
  const dl = async (r) => {
    try { await downloadQuoteFile(tenderId, quote.id, slot, r.rev, r.filename); }
    catch (e) { toast.error(formatApiError(e, "Download failed")); }
  };
  return (
    <div data-testid={`subby-details-files-${slot}`}>
      <div className="col-head mb-1" style={{ fontSize: 9.5 }}>{SLOT_LABEL[slot]}</div>
      {revs.length === 0 ? <div className="text-ink-4" style={{ fontSize: 12 }}>Not uploaded yet</div> : (
        <ul className="space-y-1">
          {[...revs].reverse().map((r) => (
            <li key={r.rev} className="flex items-center justify-between gap-2 rounded border border-line-soft bg-surface-2/40 px-2 py-1" style={{ fontSize: 11.5 }}>
              <span className="truncate text-ink"><span className="font-mono text-ink-3 mr-1.5">r{r.rev}</span>{r.filename}<span className="text-ink-3"> · {fmtBytes(r.size_bytes)} · {ukDate(r.uploaded_at)}</span></span>
              <span className="flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => onView(slot, r)} className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-accent hover:bg-surface-2" data-testid={`subby-details-view-${slot}-r${r.rev}`}><Eye size={11} /> View</button>
                <button type="button" onClick={() => dl(r)} className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-ink-2 hover:bg-surface-2" data-testid={`subby-details-download-${slot}-r${r.rev}`}><Download size={11} /></button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** LT-39 — quote details: facts, accept state, files (view / download) and the append-only internal note trail. */
export function SubbyQuoteDetailsModal({ open, onClose, tenderId, quote, onChanged, onView, onAccept, onUnaccept }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDraft(""); setBusy(false); } }, [open, quote?.id]);
  if (!quote) return null;
  const isVoid = quote.status === "void";
  const notes = [...(quote.note_entries || [])].reverse();

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await api.post(`/tenders/${tenderId}/subby-quotes/${quote.id}/notes`, { body });
      onChanged?.(r.data); setDraft("");
      toast.success("Note added");
    } catch (e) { toast.error(formatApiError(e, "Could not add the note")); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`${quote.subcontractor_name} — quote details`} testid="subby-details" maxWidth={760}
      footer={<>
        {!isVoid && (quote.accepted
          ? <QuietButton onClick={() => onUnaccept(quote)} data-testid="subby-details-unaccept">Un-accept</QuietButton>
          : <PrimaryButton onClick={() => onAccept(quote)} data-testid="subby-details-accept">Accept this quote</PrimaryButton>)}
        <QuietButton onClick={onClose} data-testid="subby-details-close-btn">Close</QuietButton>
      </>}>
      <div className="grid grid-cols-2 gap-5">
        <div className="space-y-2">
          <Row label="Amount" testid="subby-details-amount"><span className="font-mono tnum" style={{ fontSize: 15, fontWeight: 600 }}>{gbp(quote.amount)}</span> <span className="text-ink-3">ex VAT</span></Row>
          <Row label="Trade" testid="subby-details-trade">{quote.trade_name || "—"}</Row>
          <Row label="Their ref" testid="subby-details-reference">{quote.reference || "—"}</Row>
          <Row label="Quote date" testid="subby-details-date">{quote.quote_date ? ukDate(quote.quote_date) : "—"}</Row>
          <Row label="Valid until" testid="subby-details-valid">{quote.valid_until ? ukDate(quote.valid_until) : "—"}</Row>
          <Row label="Uploaded">{ukDate(quote.created_at)}{quote.created_by_name ? ` by ${quote.created_by_name}` : ""}</Row>
          <Row label="Status" testid="subby-details-status">
            {isVoid ? <span style={{ color: "#7A1F1F" }}>VOID — {quote.void_reason}</span>
              : quote.accepted ? <span style={{ color: "#2F4F3E", fontWeight: 600 }}>ACCEPTED {ukDate(quote.accepted_at)}{quote.accepted_by_name ? ` by ${quote.accepted_by_name}` : ""}</span>
              : <span className="text-ink-3">Active — not accepted</span>}
          </Row>
          {quote.notes && <Row label="On upload" testid="subby-details-initial-notes"><span className="text-ink-2">{quote.notes}</span></Row>}
        </div>
        <div className="space-y-3">
          <Files tenderId={tenderId} quote={quote} slot="original" onView={onView} />
          <Files tenderId={tenderId} quote={quote} slot="client" onView={onView} />
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-line" data-testid="subby-details-notes">
        <div className="flex items-center gap-2 mb-2">
          <StickyNote size={13} className="text-ink-3" />
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>Internal notes</span>
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>· {notes.length === 0 ? "none yet" : `${notes.length} note${notes.length === 1 ? "" : "s"}`} · never shown to the client or the subby</span>
        </div>
        <div className="flex items-start gap-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="e.g. Chased 12/09 — price holds until end of month" data-testid="subby-details-note-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
            className="flex-1 px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y" style={{ fontSize: 13, lineHeight: "18px" }} />
          <PrimaryButton onClick={addNote} disabled={!draft.trim() || busy} icon={busy ? Loader2 : Plus} data-testid="subby-details-note-add">Add note</PrimaryButton>
        </div>
        {notes.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="subby-details-note-list">
            {notes.map((n) => (
              <li key={n.id} className="rounded-md border border-line-soft bg-surface-2/40 px-3 py-2" data-testid={`subby-details-note-${n.id}`}>
                <div className="text-ink whitespace-pre-wrap" style={{ fontSize: 12.5 }}>{n.body}</div>
                <div className="text-ink-3 mt-1" style={{ fontSize: 10.5 }}>{n.author_name || "Unknown"} · {ukDateTime(n.created_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
